import React from 'react';
import { solveMultiUnitOptimization } from '../../api/api';
import { usePipeline } from '../../context/PipelineContext';
import './MultiUnitOptimization.css';

const PRESETS = {
  balanced: { label: 'Balanced', demand_met_weight: 1.0, lapsed_penalty: 0.5, unmet_penalty: 1.0 },
  maximize_demand_met: { label: 'Maximize demand met', demand_met_weight: 1.0, lapsed_penalty: 0.1, unmet_penalty: 1.5 },
  minimize_lapsed: { label: 'Minimize lapsed', demand_met_weight: 0.5, lapsed_penalty: 1.5, unmet_penalty: 0.3 },
};

// Labels only, for rendering result.post_banking.banking_type — the type
// itself is auto-detected server-side from the source run's Banking node,
// never chosen here.
const BANKING_TYPES = [
  { value: 'daily_without_tod', label: 'Daily, without TOD' },
  { value: 'monthly_without_tod', label: 'Monthly, without TOD' },
  { value: 'yearly_without_tod', label: 'Yearly, without TOD' },
  { value: 'daily_tod', label: 'Daily, TOD' },
  { value: 'monthly_tod', label: 'Monthly, TOD' },
  { value: 'yearly_tod', label: 'Yearly, TOD' },
  { value: 'policy_banking_MH_2025_corrected', label: 'Policy: Maharashtra 2025 (corrected)' },
  { value: 'policy_banking_MH_2025_new', label: 'Policy: Maharashtra 2025 (new)' },
  { value: 'policy_banking_KA_2025', label: 'Policy: Karnataka 2025' },
  { value: 'policy_banking_KA_2025_old', label: 'Policy: Karnataka 2025 (old)' },
  { value: 'policy_banking_TN_2025', label: 'Policy: Tamil Nadu 2025' },
  { value: 'policy_banking_GH_2025', label: 'Policy: GH 2025' },
];

const number = (value, fallback = 0) => (Number.isFinite(Number(value)) ? Number(value) : fallback);
const formatValue = (value, digits = 1) =>
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? '—'
    : Number(value).toLocaleString(undefined, { maximumFractionDigits: digits });
const formatPct = (value, digits = 1) => `${formatValue(value, digits)}%`;
const pctChange = (before, after) => {
  if (!Number.isFinite(before) || before === 0) return null;
  return ((after - before) / Math.abs(before)) * 100;
};

/** Headline banner card: before -> after with a signed delta, colored by whether the
 * metric's direction of movement is the "good" one for that metric. */
const BannerCard = ({ label, before, after, digits = 1, unit = '', goodDirection = 'down' }) => {
  const delta = after - before;
  const isGood = goodDirection === 'down' ? delta <= 0 : delta >= 0;
  const change = pctChange(before, after);
  return (
    <div className="mu-opt-banner-card">
      <small>{label}</small>
      <div className="mu-opt-banner-values">
        {formatValue(before, digits)}{unit} <span style={{ color: '#9db8b4' }}>→</span> <b>{formatValue(after, digits)}{unit}</b>
      </div>
      {Math.abs(delta) > 1e-9 && (
        <div className={`mu-opt-banner-delta ${isGood ? 'good' : 'bad'}`}>
          {delta > 0 ? '+' : ''}{formatValue(delta, digits)}{unit}
          {change !== null && ` (${change > 0 ? '+' : ''}${formatValue(change, 1)}%)`}
        </div>
      )}
    </div>
  );
};

const DivergingBar = ({ rows, maxAbs }) => (
  <div className="mu-opt-bars">
    {rows.map((row) => {
      const pct = maxAbs > 0 ? (Math.abs(row.mw_delta) / maxAbs) * 50 : 0;
      return (
        <div className="mu-opt-bar-row" key={row.unit_id}>
          <span title={row.unit_name || row.unit_id}>{row.unit_name || row.unit_id}</span>
          <div className="mu-opt-bar-track">
            <div className="mu-opt-bar-mid" />
            <div
              className={`mu-opt-bar-fill ${row.mw_delta >= 0 ? 'pos' : 'neg'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className={row.mw_delta >= 0 ? 'mu-opt-positive' : 'mu-opt-negative'}>
            {row.mw_delta >= 0 ? '+' : ''}{formatValue(row.mw_delta, 2)} MW
          </span>
        </div>
      );
    })}
  </div>
);

const MultiUnitOptimization = ({ onClose }) => {
  const { session } = usePipeline();
  const [sourceMode, setSourceMode] = React.useState('run'); // 'run' | 'units'
  const [runId, setRunId] = React.useState('');

  // Pre-fill from the pipeline run already open in this session, so the
  // common case ("optimize the run I just executed") needs no copy-pasting.
  React.useEffect(() => {
    if (session.last_run_id && !runId) setRunId(session.last_run_id);
  }, [session.last_run_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [unitsJson, setUnitsJson] = React.useState(
    JSON.stringify(
      {
        units: [
          { unit_id: 'u1', unit_name: 'Unit 1', balance: 2.0, consumption: [5, 5] },
          { unit_id: 'u2', unit_name: 'Unit 2', balance: 2.0, consumption: [50, 50] },
        ],
        generation_per_mw: [10, 10],
        total_mwp: 2.0,
      },
      null,
      2
    )
  );
  const [preset, setPreset] = React.useState('balanced');
  const [weights, setWeights] = React.useState(PRESETS.balanced);
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  const [timeAggregation, setTimeAggregation] = React.useState('native');
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const applyPreset = (key) => {
    setPreset(key);
    const { label, ...values } = PRESETS[key];
    setWeights(values);
  };

  const solve = async () => {
    setError('');
    setBusy(true);
    try {
      let payload;
      if (sourceMode === 'run') {
        if (!runId.trim()) throw new Error('Enter a run id to reuse an existing Multi Unit run.');
        payload = { source_run_id: runId.trim() };
      } else {
        let parsed;
        try {
          parsed = JSON.parse(unitsJson);
        } catch (parseError) {
          throw new Error('Units JSON is not valid JSON.');
        }
        payload = parsed;
      }
      payload = {
        ...payload,
        demand_met_weight: number(weights.demand_met_weight, 1.0),
        lapsed_penalty: number(weights.lapsed_penalty, 0.5),
        unmet_penalty: number(weights.unmet_penalty, 1.0),
        time_aggregation: timeAggregation,
      };
      const response = await solveMultiUnitOptimization(payload);
      setResult(response);
    } catch (requestError) {
      setError(requestError.message || 'Could not solve the allocation');
    } finally {
      setBusy(false);
    }
  };

  const maxAbsDelta = React.useMemo(
    () => (result ? Math.max(0.0001, ...result.units.map((row) => Math.abs(row.mw_delta))) : 0),
    [result]
  );

  // When a banking type was solved, post-banking figures are what actually
  // settled and are shown as the headline basis; pre-banking (matcher-level)
  // figures stay available in their own section below for transparency.
  const postBanking = result?.post_banking || null;
  const basisPortfolio = {
    first_pass: postBanking ? postBanking.first_pass_portfolio_summary : result?.first_pass_portfolio_summary,
    optimized: postBanking ? postBanking.optimized_portfolio_summary : result?.optimized_portfolio_summary,
  };
  const postBankingByUnit = React.useMemo(
    () => (postBanking ? Object.fromEntries(postBanking.units.map((row) => [row.unit_id, row])) : {}),
    [postBanking]
  );
  const displayUnits = React.useMemo(
    () =>
      result
        ? result.units.map((row) => {
            const banked = postBankingByUnit[row.unit_id];
            return banked
              ? {
                  ...row,
                  lapsed_delta: banked.lapsed_delta,
                  demand_met_delta: banked.demand_met_delta,
                  leftover_delta: banked.leftover_delta,
                  replacement_pct_delta: banked.replacement_pct_delta,
                }
              : row;
          })
        : [],
    [result, postBankingByUnit]
  );

  return (
    <main className="mu-opt-shell">
      <header className="mu-opt-topbar">
        <div>
          <p>Reallocation · second-pass LP</p>
          <h1>Multi Unit optimization</h1>
          <span>Reallocate a fixed total MWP across consumption units to maximize demand met and minimize lapsed/leftover energy.</span>
        </div>
        <button className="mu-opt-ghost" onClick={onClose}>Back to pipeline</button>
      </header>

      {error && <div className="mu-opt-error">{error}<button onClick={() => setError('')}>×</button></div>}

      <section className="mu-opt-layout">
        <aside className="mu-opt-rail">
          <div className="mu-opt-section">
            <h3>Data source</h3>
            <div className="mu-opt-segmented">
              <button className={sourceMode === 'run' ? 'active' : ''} onClick={() => setSourceMode('run')}>Existing run</button>
              <button className={sourceMode === 'units' ? 'active' : ''} onClick={() => setSourceMode('units')}>Paste units</button>
            </div>
            {sourceMode === 'run' ? (
              <label className="mu-opt-field">
                <span>Run ID</span>
                <div className="mu-opt-inline-field">
                  <input
                    className="mu-opt-input"
                    value={runId}
                    onChange={(event) => setRunId(event.target.value)}
                    placeholder="Paste a completed Multi Unit run id"
                  />
                  {session.last_run_id && (
                    <button
                      type="button"
                      className="mu-opt-inline-button"
                      onClick={() => setRunId(session.last_run_id)}
                      disabled={runId === session.last_run_id}
                      title="Use the run currently open in this session"
                    >
                      Use latest
                    </button>
                  )}
                </div>
                <p className="hint">Reuses the units, balances, consumption, and generation profile already computed by that run's Multi Consumption Allocation node.</p>
                <p className="hint">If this run has a Banking node connected, results below are shown post-banking automatically, using that node's configured policy and fee.</p>
              </label>
            ) : (
              <label className="mu-opt-field">
                <span>Units JSON</span>
                <textarea
                  className="mu-opt-textarea"
                  value={unitsJson}
                  onChange={(event) => setUnitsJson(event.target.value)}
                  spellCheck={false}
                />
                <p className="hint">units[].consumption and generation_per_mw must share the same length. total_mwp is required.</p>
              </label>
            )}
          </div>

          <div className="mu-opt-section">
            <h3>Objective weights</h3>
            <div className="mu-opt-presets">
              {Object.entries(PRESETS).map(([key, cfg]) => (
                <button
                  key={key}
                  className={`mu-opt-preset-chip ${preset === key ? 'active' : ''}`}
                  onClick={() => applyPreset(key)}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
            <button className="mu-opt-advanced-toggle" onClick={() => setAdvancedOpen((open) => !open)}>
              {advancedOpen ? 'Hide' : 'Show'} advanced sliders
            </button>
            {advancedOpen && (
              <>
                <label className="mu-opt-field">
                  <span>Demand met weight ({weights.demand_met_weight})</span>
                  <input type="range" min="0" max="2" step="0.05" value={weights.demand_met_weight}
                    onChange={(event) => { setPreset(''); setWeights({ ...weights, demand_met_weight: Number(event.target.value) }); }} />
                </label>
                <label className="mu-opt-field">
                  <span>Lapsed penalty ({weights.lapsed_penalty})</span>
                  <input type="range" min="0" max="2" step="0.05" value={weights.lapsed_penalty}
                    onChange={(event) => { setPreset(''); setWeights({ ...weights, lapsed_penalty: Number(event.target.value) }); }} />
                </label>
                <label className="mu-opt-field">
                  <span>Unmet penalty ({weights.unmet_penalty})</span>
                  <input type="range" min="0" max="2" step="0.05" value={weights.unmet_penalty}
                    onChange={(event) => { setPreset(''); setWeights({ ...weights, unmet_penalty: Number(event.target.value) }); }} />
                </label>
              </>
            )}
          </div>

          <div className="mu-opt-section">
            <h3>Time resolution</h3>
            <select className="mu-opt-select" value={timeAggregation} onChange={(event) => setTimeAggregation(event.target.value)}>
              <option value="native">Native (most accurate)</option>
              <option value="monthly">Monthly (faster)</option>
              <option value="tod">Time-of-day (faster)</option>
            </select>
            <p className="hint">Coarser resolution solves faster but is an approximation; reported metrics are always evaluated at native resolution.</p>
          </div>

          <button className="mu-opt-run" disabled={busy} onClick={solve}>{busy ? 'Solving…' : 'Solve allocation'}</button>
        </aside>

        <div className="mu-opt-results">
          {!result ? (
            <section className="mu-opt-empty">
              <h2>Compare first-pass vs. optimized allocation</h2>
              <p>Pick a data source on the left and solve to see the measured improvement on your data.</p>
            </section>
          ) : (
            <>
              {postBanking && (
                <div className="mu-opt-basis-tag">
                  Showing <strong>post-banking</strong> figures ({BANKING_TYPES.find((t) => t.value === postBanking.banking_type)?.label || postBanking.banking_type}
                  {!postBanking.banking_type.startsWith('policy_banking') && `, ${(postBanking.banking_fee * 100).toFixed(0)}% fee`}) — the pre-banking (matcher-level) basis is shown further down.
                </div>
              )}
              <div className="mu-opt-banner">
                <BannerCard label="Lapsed energy" before={basisPortfolio.first_pass.total_lapsed} after={basisPortfolio.optimized.total_lapsed} goodDirection="down" />
                <BannerCard label="Demand met" before={basisPortfolio.first_pass.total_demand_met} after={basisPortfolio.optimized.total_demand_met} goodDirection="up" />
                <BannerCard label="Leftover demand" before={basisPortfolio.first_pass.total_leftover_demand} after={basisPortfolio.optimized.total_leftover_demand} goodDirection="down" />
                <BannerCard label="Replacement %" before={basisPortfolio.first_pass.replacement_pct} after={basisPortfolio.optimized.replacement_pct} unit="%" goodDirection="up" />
                {!postBanking && (
                  <BannerCard label="Objective score" before={result.first_pass_portfolio_summary.objective_score} after={result.optimized_portfolio_summary.objective_score} goodDirection="up" />
                )}
              </div>

              {(postBanking ? postBanking.lapsed_delta : result.lapsed_delta) > 1e-6 ||
              (postBanking ? postBanking.demand_met_delta : result.demand_met_delta) < -1e-6 ||
              (postBanking ? postBanking.leftover_delta : result.leftover_delta) > 1e-6 ? (
                <div className="mu-opt-error" style={{ background: '#fffbeb', color: '#92400e', borderColor: '#fde68a' }}>
                  These weights trade one metric against another — not every metric improved. Check the objective decomposition below to see why.
                </div>
              ) : null}

              <section className="mu-opt-card">
                <div className="mu-opt-card-head"><div><small>Portfolio{postBanking ? ' · post-banking' : ''}</small><h2>First-pass vs. optimized</h2></div></div>
                <div className="mu-opt-table-scroll">
                  <table className="mu-opt-table">
                    <thead>
                      <tr><th>Metric</th><th>First-pass</th><th>Optimized</th><th>Delta</th></tr>
                    </thead>
                    <tbody>
                      {[
                        ['Total Consumption', 'total_consumption'],
                        ['Total Generation', 'total_generation'],
                        ['Total Demand Met', 'total_demand_met'],
                        ['Total Leftover Demand', 'total_leftover_demand'],
                        ['Total Lapsed', 'total_lapsed'],
                      ].map(([label, key]) => {
                        const before = basisPortfolio.first_pass[key];
                        const after = basisPortfolio.optimized[key];
                        const delta = after - before;
                        return (
                          <tr key={key}>
                            <td>{label}</td>
                            <td>{formatValue(before)}</td>
                            <td>{formatValue(after)}</td>
                            <td className={delta === 0 ? '' : delta > 0 ? 'mu-opt-positive' : 'mu-opt-negative'}>
                              {Math.abs(delta) < 1e-9 ? '—' : `${delta > 0 ? '+' : ''}${formatValue(delta)}`}
                            </td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td>Unallocated Lapsed</td>
                        <td>{formatValue(result.first_pass_portfolio_summary.unallocated_lapsed)}</td>
                        <td>{formatValue(result.optimized_portfolio_summary.unallocated_lapsed)}</td>
                        <td>—</td>
                      </tr>
                      <tr>
                        <td>Allocated MWP</td>
                        <td>{formatValue(result.first_pass_portfolio_summary.allocated_mwp)}</td>
                        <td>{formatValue(result.optimized_portfolio_summary.allocated_mwp)}</td>
                        <td>—</td>
                      </tr>
                      <tr>
                        <td>Portfolio Replacement %</td>
                        <td>{formatPct(basisPortfolio.first_pass.replacement_pct)}</td>
                        <td>{formatPct(basisPortfolio.optimized.replacement_pct)}</td>
                        <td className="mu-opt-positive">
                          {formatValue(basisPortfolio.optimized.replacement_pct - basisPortfolio.first_pass.replacement_pct, 1)} pts
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mu-opt-card">
                <div className="mu-opt-card-head"><div><small>Where the improvement came from</small><h2>Reallocation by unit</h2></div></div>
                <DivergingBar rows={result.units} maxAbs={maxAbsDelta} />
              </section>

              <section className="mu-opt-card">
                <div className="mu-opt-card-head"><div><small>Unit-level detail{postBanking ? ' · post-banking' : ''}</small><h2>{result.units.length} units, sorted by |MW moved|</h2></div></div>
                <div className="mu-opt-table-scroll">
                  <table className="mu-opt-table">
                    <thead>
                      <tr>
                        <th>Unit</th><th>Balance</th><th>First-pass MW</th><th>Optimized MW</th><th>MW Δ</th>
                        <th>Lapsed Δ</th><th>Demand-met Δ</th>{postBanking && <th>Leftover Δ</th>}<th>Replacement % Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayUnits.map((row) => (
                        <tr key={row.unit_id}>
                          <td>{row.unit_name || row.unit_id}</td>
                          <td>{formatValue(row.balance, 2)}</td>
                          <td>{formatValue(row.first_pass_mw, 3)}</td>
                          <td>{formatValue(row.optimized_mw, 3)}</td>
                          <td className={row.mw_delta === 0 ? '' : row.mw_delta > 0 ? 'mu-opt-positive' : 'mu-opt-negative'}>
                            {row.mw_delta > 0 ? '+' : ''}{formatValue(row.mw_delta, 3)}
                          </td>
                          <td className={row.lapsed_delta <= 0 ? 'mu-opt-positive' : 'mu-opt-negative'}>
                            {row.lapsed_delta > 0 ? '+' : ''}{formatValue(row.lapsed_delta)}
                          </td>
                          <td className={row.demand_met_delta >= 0 ? 'mu-opt-positive' : 'mu-opt-negative'}>
                            {row.demand_met_delta > 0 ? '+' : ''}{formatValue(row.demand_met_delta)}
                          </td>
                          {postBanking && (
                            <td className={row.leftover_delta <= 0 ? 'mu-opt-positive' : 'mu-opt-negative'}>
                              {row.leftover_delta > 0 ? '+' : ''}{formatValue(row.leftover_delta)}
                            </td>
                          )}
                          <td className={row.replacement_pct_delta >= 0 ? 'mu-opt-positive' : 'mu-opt-negative'}>
                            {row.replacement_pct_delta > 0 ? '+' : ''}{formatValue(row.replacement_pct_delta, 1)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {postBanking && (
                <section className="mu-opt-card">
                  <div className="mu-opt-card-head"><div><small>Before banking</small><h2>Pre-banking (matcher-level) portfolio</h2></div></div>
                  <div className="mu-opt-table-scroll">
                    <table className="mu-opt-table">
                      <thead>
                        <tr><th>Metric</th><th>First-pass</th><th>Optimized</th><th>Delta</th></tr>
                      </thead>
                      <tbody>
                        {[
                          ['Total Demand Met', 'total_demand_met'],
                          ['Total Leftover Demand', 'total_leftover_demand'],
                          ['Total Lapsed', 'total_lapsed'],
                          ['Unallocated Lapsed', 'unallocated_lapsed'],
                        ].map(([label, key]) => {
                          const before = result.first_pass_portfolio_summary[key];
                          const after = result.optimized_portfolio_summary[key];
                          const delta = after - before;
                          return (
                            <tr key={key}>
                              <td>{label}</td>
                              <td>{formatValue(before)}</td>
                              <td>{formatValue(after)}</td>
                              <td className={delta === 0 ? '' : delta > 0 ? 'mu-opt-positive' : 'mu-opt-negative'}>
                                {Math.abs(delta) < 1e-9 ? '—' : `${delta > 0 ? '+' : ''}${formatValue(delta)}`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="hint" style={{ marginTop: '10px' }}>
                    The matcher-level result before this run's banking policy carries lapsed energy forward against future deficits.
                  </p>
                </section>
              )}

              <section className="mu-opt-card">
                <div className="mu-opt-card-head"><div><small>Weight tuning</small><h2>Objective decomposition</h2></div></div>
                <div className="mu-opt-table-scroll">
                  <table className="mu-opt-table">
                    <thead>
                      <tr><th>Component</th><th>First-pass</th><th>Optimized</th><th>Delta</th></tr>
                    </thead>
                    <tbody>
                      {result.objective_components.map((component) => (
                        <tr key={component.label}>
                          <td>{component.label}</td>
                          <td>{formatValue(component.first_pass)}</td>
                          <td>{formatValue(component.optimized)}</td>
                          <td className={component.delta >= 0 ? 'mu-opt-positive' : 'mu-opt-negative'}>
                            {component.delta > 0 ? '+' : ''}{formatValue(component.delta)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="hint" style={{ marginTop: '10px' }}>
                  Solved in {formatValue(result.solve_seconds, 3)}s at {result.time_aggregation} resolution.
                  {postBanking && ' The optimizer’s own objective is always pre-banking (banking is evaluated afterward on the chosen allocation), so this decomposition uses matcher-level figures even when post-banking results are shown above.'}
                </p>
              </section>
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default MultiUnitOptimization;
